import { tenantApi } from '@shared/api/tenantApi';
import { unwrapList } from '@shared/api/unwrap';

/**
 * Information directory. It uses the same ContactGroup model as Management:
 * directory groups are contact groups where `allow_member_link = false`.
 */

export interface DirectoryContact {
  id: string;
  name: string;
  role?: string;
  mobile?: string;
  email?: string;
  hours?: string;
  profile_image_url?: string;
}

export interface DirectoryGroup {
  id: string;
  name: string;
  contacts: DirectoryContact[];
}

interface RawGroup {
  contact_group_id?: string;
  name?: string;
}

interface RawContact {
  contact_id?: string;
  title?: string | null;
  first_name?: string | null;
  surname?: string | null;
  heading?: string | null;
  designation?: string | null;
  description?: string | null;
  mobile_number?: string | null;
  home_number?: string | null;
  additional_number?: string | null;
  email?: string | null;
  picture_url?: string | null;
  show_name?: boolean;
  show_email?: boolean;
  show_phone?: boolean;
}

function contactName(contact: RawContact): string {
  const nameParts = [contact.title, contact.first_name, contact.surname].filter(Boolean);
  if (contact.show_name !== false && nameParts.length > 0) {
    return nameParts.join(' ').trim();
  }
  return contact.heading || 'Contact';
}

function toContact(contact: RawContact, key: string): DirectoryContact {
  return {
    id: contact.contact_id ?? key,
    name: contactName(contact),
    role: contact.designation ?? contact.description ?? contact.heading ?? undefined,
    mobile: contact.show_phone === false
      ? undefined
      : contact.mobile_number ?? contact.home_number ?? contact.additional_number ?? undefined,
    email: contact.show_email === false ? undefined : contact.email ?? undefined,
    profile_image_url: contact.picture_url ?? undefined,
  };
}

export async function fetchDirectoryGroups(): Promise<DirectoryGroup[]> {
  const groupRes = await tenantApi.get('/contact-groups/directory');
  const groups = unwrapList<RawGroup>(groupRes.data);

  return (await Promise.all(
    groups.map(async (group, groupIndex) => {
      const groupId = group.contact_group_id;
      if (!groupId) {
        return {
          id: `group-${groupIndex}`,
          name: group.name ?? 'Directory',
          contacts: [],
        };
      }

      const contactsRes = await tenantApi.get(`/contact-groups/${groupId}/contacts/member`);
      const contacts = unwrapList<RawContact>(contactsRes.data);
      return {
        id: groupId,
        name: group.name ?? 'Directory',
        contacts: contacts.map((contact, contactIndex) => toContact(contact, `c-${groupIndex}-${contactIndex}`)),
      };
    }),
  )).filter((group) => group.contacts.length > 0);
}
